import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal, openModal } from 'data/reducers/modalReducer';
import {
	fetchUserAssignedProperties,
	assignUsersToProperty,
	removeUserFromProperty,
	removeUserFromPortfolio,
	getPortfolioProperties,
	clearPortfolioErrors,
	fetchPortfolioById,
} from 'data/reducers/portfoliosManagementReducer';
import { showToast } from 'data/reducers/toastReducer';
import { MODAL_TYPES } from '../ModalManager';
import StandardModal from './StandardModal';
import './PortfolioUserViewModal.css';

const PortfolioUserViewModal = ({ options }) => {
	const dispatch = useDispatch();

	const {
		allProperties: portfolioProperties,
		selectedPortfolio,
	} = useSelector((state) => state.portfoliosManagement);

	// Portfolio details
	const portfolioSetId = options?.portfolioSetId;
	const portfolioName =
		options?.portfolioName ||
		options?.portfolio?.name ||
		selectedPortfolio?.name ||
		'Portfolio';
	const hasAdminAccess = options?.hasAdminAccess ?? false;

	// User list to display (placing clicked user at the top)
	const usersList = useMemo(() => {
		let list = [];
		if (Array.isArray(options?.users) && options.users.length > 0) {
			list = [...options.users];
		} else if (options?.user) {
			list = [options.user];
		}
		if (options?.user?.id && list.length > 1) {
			const idx = list.findIndex((u) => u.id === options.user.id);
			if (idx > 0) {
				const [clickedUser] = list.splice(idx, 1);
				list.unshift(clickedUser);
			}
		}
		return list;
	}, [options?.users, options?.user]);

	// Currently expanded user ID
	const [expandedUserId, setExpandedUserId] = useState(
		options?.user?.id || usersList[0]?.id || null,
	);

	// Cached properties per user { [userId]: propertyList }
	const [userPropertiesMap, setUserPropertiesMap] = useState({});
	const [loadingPropertiesMap, setLoadingPropertiesMap] = useState({});

	// Selected property in "Assign New Property" dropdown
	const [selectedPropertyId, setSelectedPropertyId] = useState('');
	const [isAssigning, setIsAssigning] = useState(false);

	// Helper to determine role badge and access level text
	const getUserRole = (u) => {
		const role = u?.pivot?.user_type || u?.pivot?.role || u?.role;
		if (role === 'admin' || role === 'owner') return 'Admin';
		return 'Viewer';
	};

	const getUserAccessLevel = (u) => {
		const role = u?.pivot?.user_type || u?.pivot?.role || u?.role;
		if (role === 'admin' || role === 'owner') {
			return 'Can manage portfolio and properties';
		}
		return 'Can view assigned properties';
	};

	// Fetch assigned properties for a user
	const loadAssignedPropertiesForUser = useCallback(
		async (userId) => {
			if (!userId || !portfolioSetId) return;
			setLoadingPropertiesMap((prev) => ({ ...prev, [userId]: true }));
			try {
				const result = await dispatch(
					fetchUserAssignedProperties(portfolioSetId, userId),
				);
				const propsList = Array.isArray(result)
					? result
					: result?.data || result?.properties || [];
				setUserPropertiesMap((prev) => ({
					...prev,
					[userId]: propsList,
				}));
			} catch (error) {
				console.error('Error fetching assigned properties:', error);
			} finally {
				setLoadingPropertiesMap((prev) => ({ ...prev, [userId]: false }));
			}
		},
		[dispatch, portfolioSetId],
	);

	// Load all properties for assignment dropdown
	useEffect(() => {
		dispatch(clearPortfolioErrors());
		if (hasAdminAccess && portfolioSetId) {
			dispatch(getPortfolioProperties(portfolioSetId, 1, 10000));
		}
	}, [dispatch, hasAdminAccess, portfolioSetId]);

	// Load properties whenever expanded user changes
	useEffect(() => {
		if (expandedUserId) {
			loadAssignedPropertiesForUser(expandedUserId);
			setSelectedPropertyId('');
		}
	}, [expandedUserId, loadAssignedPropertiesForUser]);

	// Format available properties for dropdown (exclude already assigned)
	const availablePropertyOptions = useMemo(() => {
		const assignedForExpanded = userPropertiesMap[expandedUserId] || [];
		const assignedIds = assignedForExpanded.map((p) => p.portfolio_id || p.id);
		return (portfolioProperties || [])
			.filter((p) => {
				const id = p.portfolio_id || p.id;
				return !assignedIds.includes(id);
			})
			.map((property) => ({
				value: property.portfolio_id || property.id,
				label: property.address || `${property.block}/${property.lot}`,
			}));
	}, [portfolioProperties, userPropertiesMap, expandedUserId]);

	// Form submission for assigning property
	const handleAssignProperty = async (e) => {
		if (e) e.preventDefault();
		if (!selectedPropertyId || !expandedUserId) return;

		setIsAssigning(true);
		try {
			await dispatch(
				assignUsersToProperty(portfolioSetId, selectedPropertyId, [
					expandedUserId,
				]),
			);

			dispatch(
				showToast({
					title: 'Success!',
					body: 'Property has been assigned successfully.',
					type: 'success',
				}),
			);

			setSelectedPropertyId('');
			loadAssignedPropertiesForUser(expandedUserId);
		} catch (error) {
			console.error('Error assigning property:', error);
			dispatch(
				showToast({
					title: 'Error!',
					body:
						error.response?.data?.message ||
						error.message ||
						'Failed to assign property. Please try again.',
					type: 'error',
				}),
			);
		} finally {
			setIsAssigning(false);
		}
	};

	// Cancel assigning property
	const handleCancelAssign = () => {
		setSelectedPropertyId('');
	};

	// Remove property access from user
	const handleRemoveProperty = (property, targetUser) => {
		dispatch(
			openModal(MODAL_TYPES.CONFIRM, {
				color: 'danger',
				title: 'Remove Property Access',
				children: (
					<div>
						Are you sure you want to remove {targetUser.name}'s access to "
						{property.address}"? They will no longer be able to view this
						property.
					</div>
				),
				confirmLabel: 'Remove Access',
				onConfirm: async () => {
					try {
						await dispatch(
							removeUserFromProperty(
								portfolioSetId,
								property.portfolio_id || property.id,
								targetUser.id,
							),
						);

						dispatch(
							showToast({
								title: 'Success!',
								body: `${targetUser.name}'s access to "${property.address}" has been removed.`,
								type: 'success',
							}),
						);

						loadAssignedPropertiesForUser(targetUser.id);
					} catch (error) {
						console.error('Error removing property:', error);
						dispatch(
							showToast({
								title: 'Error!',
								body: 'Failed to remove property access. Please try again.',
								type: 'error',
							}),
						);
					}
				},
			}),
		);
	};

	// Remove user from portfolio
	const handleDeleteUser = (targetUser, e) => {
		if (e) e.stopPropagation();
		dispatch(
			openModal(MODAL_TYPES.CONFIRM, {
				color: 'danger',
				title: 'Remove User',
				children: (
					<div>
						Are you sure you want to remove <strong>{targetUser.name}</strong> from
						this portfolio?
					</div>
				),
				confirmLabel: 'Remove User',
				onConfirm: async () => {
					try {
						await dispatch(removeUserFromPortfolio(portfolioSetId, targetUser.id));
						dispatch(
							showToast({
								title: 'Success!',
								body: `${targetUser.name} has been removed from the portfolio.`,
								type: 'success',
							}),
						);
						dispatch(fetchPortfolioById(portfolioSetId));
						if (expandedUserId === targetUser.id) {
							setExpandedUserId(null);
						}
					} catch (error) {
						console.error('Error removing user:', error);
						dispatch(
							showToast({
								title: 'Error!',
								body: 'Failed to remove user from portfolio. Please try again.',
								type: 'error',
							}),
						);
					}
				},
			}),
		);
	};

	const handleCancel = () => {
		dispatch(closeModal());
	};

	if (usersList.length === 0) {
		return (
			<StandardModal
				title="Portfolio User Details"
				subtitle={portfolioName}
				className="violerts-v3-root portfolio-user-view-modal"
				onClose={handleCancel}
				closeOnOutsideClick
				noFooter
			>
				<div className="p-4 text-center text-muted">
					No users available in this portfolio.
				</div>
				<div className="portfolio-user-modal-footer">
					<button
						type="button"
						className="portfolio-user-modal-close-btn"
						onClick={handleCancel}
					>
						Close
					</button>
				</div>
			</StandardModal>
		);
	}

	return (
		<StandardModal
			title="Portfolio User Details"
			subtitle={portfolioName}
			className="violerts-v3-root portfolio-user-view-modal"
			onClose={handleCancel}
			closeOnOutsideClick
			noFooter
		>
			<div className="portfolio-user-modal-body">
				{usersList.map((u) => {
					const isExpanded = expandedUserId === u.id;
					const role = getUserRole(u);
					const accessLevel = getUserAccessLevel(u);
					const userProps = userPropertiesMap[u.id] || [];
					const isLoadingProps = loadingPropertiesMap[u.id];
					const countStr = String(userProps.length).padStart(2, '0');
					const isUserOwner =
						u.role === 'owner' ||
						u.pivot?.role === 'owner' ||
						u.pivot?.user_type === 'owner';

					return (
						<div
							key={u.id}
							className={`portfolio-user-accordion-item ${
								isExpanded ? 'expanded' : ''
							}`}
						>
							<div
								className="portfolio-user-header-row"
								onClick={() =>
									setExpandedUserId(isExpanded ? null : u.id)
								}
								role="button"
								tabIndex={0}
								onKeyDown={(e) => {
									if (e.key === ' ' || e.key === 'Enter') {
										e.preventDefault();
										setExpandedUserId(isExpanded ? null : u.id);
									}
								}}
							>
								<button
									type="button"
									className="portfolio-user-chevron-btn"
									aria-label={isExpanded ? 'Collapse user' : 'Expand user'}
								>
									<i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`} />
								</button>

								<div className="portfolio-user-grid">
									<div className="portfolio-user-grid-col">
										<div className="portfolio-user-field-label">Name</div>
										<div className="portfolio-user-name-val">
											{u.name || 'User'}
										</div>
										<div className="portfolio-user-field-spacer" />
										<div className="portfolio-user-field-label">
											Role in Portfolio
										</div>
										<div>
											<span
												className={`portfolio-user-role-badge ${role.toLowerCase()}`}
											>
												{role}
											</span>
										</div>
									</div>

									<div className="portfolio-user-grid-col">
										<div className="portfolio-user-field-label">Email</div>
										<div className="portfolio-user-email-val">
											{u.email || 'N/A'}
										</div>
										<div className="portfolio-user-field-spacer" />
										<div className="portfolio-user-field-label">
											Access Level
										</div>
										<div className="portfolio-user-access-val">
											{accessLevel}
										</div>
									</div>
								</div>

								<div className="portfolio-user-action-col">
									{hasAdminAccess && !isUserOwner && (
													<>
														<button
															type="button"
															className="portfolio-user-edit-btn"
															onClick={(e) => {
																e.stopPropagation();
																dispatch(
																	openModal(MODAL_TYPES.ADD_PORTFOLIO_USER_MODAL, {
																		portfolioId: portfolioSetId,
																		existingUser: u,
																	}),
																);
															}}
															title="Edit user role"
														>
															<i className="bi bi-pencil" />
														</button>
														<button
															type="button"
															className="portfolio-user-delete-btn"
															onClick={(e) => handleDeleteUser(u, e)}
															title="Remove user from portfolio"
														>
															<i className="bi bi-trash" />
														</button>
													</>
									)}
								</div>
							</div>

							{isExpanded && (
								<div className="portfolio-user-expanded-content">
									<h6 className="portfolio-user-assigned-heading">
										Assigned Properties ({countStr})
									</h6>

									{hasAdminAccess && (
										<div className="portfolio-assign-property-card">
											<div className="portfolio-assign-property-card-title">
												Assign New Property
											</div>
											<div className="portfolio-assign-select-wrapper">
												<select
													className="portfolio-assign-select"
													value={selectedPropertyId}
													onChange={(e) =>
														setSelectedPropertyId(e.target.value)
													}
													disabled={isAssigning}
												>
													<option value="">Select Property</option>
													{availablePropertyOptions.map((opt) => (
														<option key={opt.value} value={opt.value}>
															{opt.label}
														</option>
													))}
												</select>
											</div>
											<div className="portfolio-assign-btns-row">
												<button
													type="button"
													className="portfolio-assign-submit-btn"
													onClick={handleAssignProperty}
													disabled={!selectedPropertyId || isAssigning}
												>
													{isAssigning ? (
														'Assigning...'
													) : (
														<>
															<span>Assign Property</span>
															<span className="portfolio-assign-plus-icon">+</span>
														</>
													)}
												</button>
												<button
													type="button"
													className="portfolio-assign-cancel-btn"
													onClick={handleCancelAssign}
													disabled={isAssigning}
												>
													Cancel
												</button>
											</div>
										</div>
									)}

									{isLoadingProps ? (
										<div className="text-center py-3">
											<span
												className="spinner-border spinner-border-sm text-secondary"
												role="status"
											/>
											<span className="text-muted ms-2 small">
												Loading properties...
											</span>
										</div>
									) : userProps.length > 0 ? (
										<div className="portfolio-user-properties-list">
											{userProps.map((prop) => (
												<div
													key={prop.portfolio_id || prop.id}
													className="portfolio-assigned-prop-card"
												>
													<div className="portfolio-assigned-prop-info">
														<div className="portfolio-assigned-prop-address">
															{prop.address || 'Property Address'}
														</div>
														<div className="portfolio-assigned-prop-meta">
															BIN: {prop.bin || 'N/A'} • Block:{' '}
															{prop.block || 'N/A'} • Lot:{' '}
															{prop.lot || 'N/A'}
														</div>
													</div>
													{hasAdminAccess && (
														<button
															type="button"
															className="portfolio-prop-delete-btn"
															onClick={() =>
																handleRemoveProperty(prop, u)
															}
															title="Remove property access"
														>
															<i className="bi bi-trash" />
														</button>
													)}
												</div>
											))}
										</div>
									) : (
										<div className="portfolio-user-no-properties text-muted text-center py-3 small">
											No properties assigned to this user yet.
										</div>
									)}
								</div>
							)}
						</div>
					);
				})}
			</div>

			<div className="portfolio-user-modal-footer">
				<button
					type="button"
					className="portfolio-user-modal-close-btn"
					onClick={handleCancel}
				>
					Close
				</button>
			</div>
		</StandardModal>
	);
};

export default PortfolioUserViewModal;
